function isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
}

export const mergeDeep = (target: any, source: any): any => {
    if (!source) return target;
    if (!target) return source;

    let output = Object.assign({}, target);

    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach(key => {
            if (isObject(source[key])) {
                if (!(key in target)) {
                    Object.assign(output, { [key]: source[key] });
                } else {
                    output[key] = mergeDeep(target[key], source[key]);
                }
            } else if (Array.isArray(source[key])) {
                if (Array.isArray(target[key])) {
                    if (key === 'pages') {
                        output[key] = mergePageArrays(target[key], source[key]);
                    } else if (key === 'modules') {
                        output[key] = mergeModuleArrays(target[key], source[key]);
                    } else {
                        // For other arrays: deduplicate primitives, merge objects
                        output[key] = mergeArrays(target[key], source[key]);
                    }
                } else {
                    output[key] = source[key];
                }
            } else {
                Object.assign(output, { [key]: source[key] });
            }
        });
    }

    return output;
}

function mergePageArrays(targetPages: any[], sourcePages: any[]): any[] {
    const merged = [...targetPages];
    const targetMap = new Map();

    // Create map using first path element as key
    targetPages.forEach((page, index) => {
        const key = page.id || getPageKey(page.path);
        targetMap.set(key, index);
    });

    sourcePages.forEach(sourcePage => {
        const key = sourcePage.id || getPageKey(sourcePage.path);

        if (targetMap.has(key)) {
            // Page exists - deep merge without array concatenation
            const targetIndex = targetMap.get(key);
            merged[targetIndex] = mergePageObject(merged[targetIndex], sourcePage);
        } else {
            // New page - add it
            merged.push(sourcePage);
        }
    });

    return merged;
}

function mergePageObject(targetPage: any, sourcePage: any): any {
    const result = { ...targetPage };

    Object.keys(sourcePage).forEach(key => {
        if (key === 'path') {
            // For paths: use source value (don't concatenate)
            result[key] = sourcePage[key];
        } else if (key === 'modules') {
            // For modules: use source value (don't merge arrays)
            result[key] = sourcePage[key];
        } else if (isObject(sourcePage[key])) {
            // Deep merge objects
            result[key] = isObject(targetPage[key])
                ? mergeDeep(targetPage[key], sourcePage[key])
                : sourcePage[key];
        } else {
            // Primitives: source wins
            result[key] = sourcePage[key];
        }
    });

    return result;
}

function mergeModuleArrays(targetModules: any[], sourceModules: any[]): any[] {
    // If source has string modules, use it directly
    if (sourceModules.some(m => typeof m === 'string')) {
        return sourceModules;
    }

    const merged = [...targetModules];
    const targetMap = new Map();

    targetModules.forEach((mod, index) => {
        const modName = typeof mod === 'string' ? mod : mod.module;
        if (modName) {
            targetMap.set(modName, index);
        }
    });

    sourceModules.forEach(sourceMod => {
        const modName = typeof sourceMod === 'string' ? sourceMod : sourceMod.module;

        if (modName && targetMap.has(modName)) {
            const targetIndex = targetMap.get(modName);
            // If both are objects, merge them
            if (isObject(sourceMod) && isObject(targetModules[targetIndex])) {
                merged[targetIndex] = mergeDeep(merged[targetIndex], sourceMod);
            } else {
                // Otherwise, source wins
                merged[targetIndex] = sourceMod;
            }
        } else {
            merged.push(sourceMod);
        }
    });

    return merged;
}

function mergeArrays(targetArr: any[], sourceArr: any[]): any[] {
    // For primitive arrays: deduplicate
    if (targetArr.every(item => !isObject(item) && !Array.isArray(item))) {
        // return [...new Set([...targetArr, ...sourceArr])];
        return Array.from(new Set([...targetArr, ...sourceArr]));
    }
    // For object arrays: just use source
    return sourceArr;
}

function getPageKey(path: string | string[]): string {
    if (Array.isArray(path)) {
        return path[0];
    }
    return path;
}

export default mergeDeep;